import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, FileDown } from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { listExpenses, exportExpensesCsvUrl } from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import { EXPENSE } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";

const STATUSES = ["all", "pending", "info_requested", "approved", "rejected", "reimbursed"];

function money(n, c = "USD") {
  return `${c === "USD" ? "$" : c + " "}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function Expenses() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    listExpenses().then((d) => { if (active) { setItems(d); setLoading(false); } }).catch(() => setLoading(false));
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => filter === "all" ? items : items.filter((e) => e.status === filter), [items, filter]);

  return (
    <>
      <PageHeader title="Expenses" subtitle={user?.role === "volunteer" ? "Receipts you've submitted." : "Every receipt from every volunteer."}>
        <button
          onClick={() => {
            const tok = localStorage.getItem("td_session");
            fetch(exportExpensesCsvUrl("trustdime"), { headers: { Authorization: `Bearer ${tok}` } })
              .then((r) => r.blob())
              .then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "expenses.csv"; a.click(); });
          }}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background hover:bg-accent px-3 py-2 text-sm"
        >
          <FileDown size={14} /> Export CSV
        </button>
        <Link
          to="/app/expenses/new"
          data-testid={EXPENSE.newButton}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90"
        >
          New expense <ArrowUpRight size={14} />
        </Link>
      </PageHeader>
      <PageBody>
        <div className="flex gap-2 mb-5 overflow-x-auto">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`expense-filter-${s}`}
              className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-wider transition-colors ${
                filter === s ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {s.replace("_", " ")}
            </button>
          ))}
        </div>

        <div data-testid={EXPENSE.table} className="border border-border bg-card rounded-md overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-accent/40 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            <div className="col-span-3">Vendor</div>
            <div className="col-span-2">Program</div>
            <div className="col-span-2">Submitted by</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1 text-right">Amount</div>
            <div className="col-span-2 text-right">Status</div>
          </div>

          {loading ? (
            <div className="px-6 py-16 text-center text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="px-6 py-20 text-center">
              <FileText className="mx-auto text-muted-foreground" size={28} strokeWidth={1.2} />
              <div className="serif text-xl mt-3 text-primary">No expenses here yet</div>
              <p className="text-sm text-muted-foreground mt-1">
                {filter === "all" ? "Start your ledger with a single receipt." : `No expenses with status "${filter}".`}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((e) => (
                <Link key={e.id} to={`/app/expenses/${e.id}`} data-testid={EXPENSE.row(e.id)} className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-accent/30 transition-colors">
                  <div className="col-span-3 min-w-0">
                    <div className="text-sm font-medium truncate">{e.vendor}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">{e.category}</div>
                  </div>
                  <div className="col-span-2 text-sm truncate">{e.program_name}</div>
                  <div className="col-span-2 text-sm truncate text-muted-foreground">{e.submitted_by_name}</div>
                  <div className="col-span-2 text-sm mono">{e.spent_on}</div>
                  <div className="col-span-1 text-right mono text-sm text-primary tabular">{money(e.amount, e.currency)}</div>
                  <div className="col-span-2 flex justify-end"><StatusPill status={e.status} testId={EXPENSE.status(e.id)} /></div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </PageBody>
    </>
  );
}
